import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from "@/components/ui/button";
import { Calendar, ChevronRight } from "lucide-react";
import { SwipeableDialog as Dialog, SwipeableDialogContent as DialogContent, SwipeableDialogHeader as DialogHeader, SwipeableDialogTitle as DialogTitle, SwipeableDialogTrigger as DialogTrigger } from "@/components/ui/swipeable-dialog";
import { EventList } from "@/components/EventList";
import { useLanguage } from "@/contexts/LanguageContext";

export const EventTicker = () => {
    const [nextEvent, setNextEvent] = useState(null);
    const { language } = useLanguage();

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/events`);
                const now = new Date();
                const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                const upcoming = response.data
                    .filter(e => {
                        const eventDate = new Date(e.date);
                        const eventDay = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
                        return eventDay >= today;
                    })
                    .sort((a, b) => new Date(a.date) - new Date(b.date));

                if (upcoming.length > 0) {
                    setNextEvent(upcoming[0]);
                }
            } catch (error) {
                console.error("Failed to fetch events", error);
            }
        };
        fetchEvents();
    }, []);

    // Dispatch custom event when ticker is hovered to move Award Badge up
    const handleMouseEnter = () => {
        window.dispatchEvent(new CustomEvent('tickerHover', { detail: { hovered: true } }));
    };

    const handleMouseLeave = () => {
        window.dispatchEvent(new CustomEvent('tickerHover', { detail: { hovered: false } }));
    };

    if (!nextEvent) return null;

    return (
        <Dialog>
            <DialogTrigger asChild>
                <div
                    className="fixed bottom-2 right-2 md:bottom-8 md:right-8 z-30 animate-in slide-in-from-bottom-10 fade-in duration-700 cursor-pointer group max-w-[45vw] md:max-w-none"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    role="button"
                    tabIndex={0}
                    aria-label={`Nächstes Event: ${nextEvent.title || ''}`}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); } }}
                >
                    <div className="relative flex flex-col items-end">
                        {/* Balanced Pulse Effect - visible but not annoying */}
                        <span
                            className="absolute inline-flex h-full w-full rounded-2xl bg-gray-400 group-hover:opacity-0 transition-opacity hidden md:inline-flex"
                            style={{
                                animation: 'balancedPulse 2.5s ease-in-out infinite'
                            }}
                        ></span>
                        <style>{`
                            @keyframes balancedPulse {
                                0%, 100% { transform: scale(1); opacity: 0.25; }
                                50% { transform: scale(1.06); opacity: 0.45; }
                            }
                        `}</style>

                        {/* The Expandable Card/Button - Much smaller on mobile */}
                        <div className="relative overflow-hidden bg-black/90 backdrop-blur-md hover:bg-black text-white rounded-xl md:rounded-2xl shadow-lg md:shadow-xl border border-white/20 transition-all duration-500 ease-[cubic-bezier(0.25,1,0.5,1)] group-hover:scale-105 group-hover:border-white/40 group-hover:shadow-2xl flex flex-col w-[130px] md:w-[200px]">

                            {/* Image Preview (Hidden by default, expands on hover) */}
                            {nextEvent.flyer_url && (
                                <div className="h-0 opacity-0 group-hover:h-32 group-hover:opacity-100 transition-all duration-500 ease-in-out w-full overflow-hidden">
                                    <img
                                        src={nextEvent.flyer_url}
                                        alt="Event Preview"
                                        className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                </div>
                            )}

                            {/* Text Content */}
                            <div className="flex items-center gap-2 md:gap-3 px-2 py-2 md:px-4 md:py-3">
                                <div className="flex-shrink-0 bg-white/20 p-1.5 md:p-2 rounded-full border border-white/20">
                                    <Calendar className="h-3 w-3 md:h-4 md:w-4 text-white" />
                                </div>

                                <div className="flex-1 min-w-0 flex flex-col">
                                    <div className="flex justify-between items-center mb-0.5">
                                        <div className="text-[8px] md:text-[9px] uppercase tracking-wider md:tracking-widest text-white/70 font-bold">
                                            Event
                                        </div>
                                        <div className="bg-white/20 rounded px-1 md:px-1.5 py-0.5 text-[8px] md:text-[9px] font-mono text-white border border-white/20">
                                            {new Date(nextEvent.date).toLocaleDateString(language, { day: '2-digit', month: '2-digit' })}
                                        </div>
                                    </div>

                                    <div className="text-[10px] md:text-xs font-bold text-white truncate group-hover:whitespace-normal group-hover:text-clip leading-tight">
                                        {nextEvent.title}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </DialogTrigger>
            <DialogContent
              className="w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto bg-white border-none p-0 rounded-none"
              onInteractOutside={(e) => { const t = e.detail?.originalEvent?.target; if (t?.closest?.('[data-lightbox]')) e.preventDefault(); }}
              onEscapeKeyDown={(e) => { if (document.querySelector('[data-lightbox]')) e.preventDefault(); }}
            >
                <div className="p-6 md:p-8">
                    <DialogHeader className="mb-6 border-b border-concrete-200 pb-4">
                        <DialogTitle className="text-2xl md:text-3xl font-bold font-heading text-center text-black">
                            {language === 'en' ? 'Event Calendar' : 'Eventkalender'}
                        </DialogTitle>
                    </DialogHeader>
                    <EventList />
                </div>
            </DialogContent>
        </Dialog>
    );
};
